import { useEffect, useRef, useCallback } from 'react'
import { useSession } from 'next-auth/react'
import { AppSession } from 'app/app/api/auth/auth'
import { useNetwork } from './useNetwork'
import { SSEMessageType } from 'app/app/common/sse/sse-utils'
import { notify } from 'app/app/bugsnag/bugsnag.client'

/**
 * Message handler type - returns true if message was handled, false otherwise
 */
export type SSEMessageHandler = (message: SSEMessageType) => boolean | void

/**
 * Hook configuration options
 */
export interface UseSSEOptions {
  autoConnect?: boolean
  maxReconnectAttempts?: number
  reconnectDelay?: number
}

export function useSSE(options: UseSSEOptions = {}) {
  const { autoConnect = true, maxReconnectAttempts = 5, reconnectDelay = 1000 } = options

  const { data: session } = useSession()
  const isOnline = useNetwork()
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isCleaningUpRef = useRef(false)
  const isConnectedRef = useRef(false)
  const wasOfflineRef = useRef(false)

  const handlersRef = useRef<Map<string, Set<SSEMessageHandler>>>(new Map())

  const logError = useCallback((message: string) => {
    notify(new Error(message))
  }, [])

  const subscribe = useCallback((messageType: string, handler: SSEMessageHandler) => {
    if (!handlersRef.current.has(messageType)) {
      handlersRef.current.set(messageType, new Set())
    }

    handlersRef.current.get(messageType)!.add(handler)
  }, [])

  const unsubscribe = useCallback((messageType: string, handler: SSEMessageHandler) => {
    const handlers = handlersRef.current.get(messageType)
    if (handlers) {
      handlers.delete(handler)
      if (handlers.size === 0) {
        handlersRef.current.delete(messageType)
      }
    }
  }, [])

  const dispatchMessage = useCallback(
    (message: SSEMessageType) => {
      const handlers = handlersRef.current.get(message.type)
      if (!handlers || handlers.size === 0) {
        logError(`No handlers registered for message type: ${message.type}`)
        return
      }

      handlers.forEach(handler => {
        try {
          handler(message)
        } catch (error) {
          logError(`Error in handler for ${error} ${message.type}`)
        }
      })
    },
    [logError]
  )

  const createConnection = useCallback(() => {
    const appSession = session as AppSession | undefined
    const userId = appSession?.profile?.user_id

    if (!userId || isCleaningUpRef.current) {
      return
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const eventSource = new EventSource(`/api/sse?userId=${userId}`)
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      isConnectedRef.current = true
      reconnectAttemptsRef.current = 0
    }

    eventSource.onerror = error => {
      logError(`SSE connection error occurred ${error}`)
      isConnectedRef.current = false
      if (eventSource.readyState !== EventSource.CLOSED) {
        eventSource.close()
      }

      if (isCleaningUpRef.current) {
        return
      }

      if (reconnectAttemptsRef.current === 0) {
        logError('SSE connection lost, attempting to reconnect...')
      }

      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++
        const delay = reconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1)

        logError(`Reconnect attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${delay}ms`)

        reconnectTimeoutRef.current = setTimeout(() => {
          createConnection()
        }, delay)
      } else {
        logError('Max reconnection attempts reached - server may be down')
      }
    }

    eventSource.onmessage = event => {
      try {
        const data = JSON.parse(event.data)

        if (data.type === 'connected') {
          return
        }

        const message = data as SSEMessageType
        dispatchMessage(message)
      } catch (error) {
        logError(`Error parsing SSE message: ${error}`)
      }
    }
  }, [session, logError, dispatchMessage, maxReconnectAttempts, reconnectDelay])

  const connect = useCallback(() => {
    if (!isCleaningUpRef.current) {
      createConnection()
    }
  }, [createConnection])

  const disconnect = useCallback(() => {
    isCleaningUpRef.current = true
    isConnectedRef.current = false

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }, [])

  useEffect(() => {
    const appSession = session as AppSession | undefined

    if (autoConnect && appSession?.profile?.user_id) {
      isCleaningUpRef.current = false
      createConnection()
    }

    return () => {
      disconnect()
    }
  }, [session, autoConnect, createConnection, disconnect])

  useEffect(() => {
    const appSession = session as AppSession | undefined
    const userId = appSession?.profile?.user_id

    if (!userId) {
      return
    }

    if (!isOnline) {
      wasOfflineRef.current = true
      return
    }

    if (isOnline && wasOfflineRef.current) {
      wasOfflineRef.current = false
      reconnectAttemptsRef.current = 0

      if (!isConnectedRef.current) {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }

        isCleaningUpRef.current = false
        createConnection()
      }
    }
  }, [isOnline, session, createConnection])

  return {
    isConnected: isConnectedRef.current,
    subscribe,
    unsubscribe,
    connect,
    disconnect
  }
}
